import { PaymentConstants } from './paymentConstants'
import {
    calculateItemCost,
    getVariantAttributes,
    hasRequiredFields,
    hasValidFields,
    getDisabledVariants,
} from './paymentUtils'
import { Map, List } from 'immutable'
import {
    GOTO_ITEM_DETAILS_PAGE,
    CHANGE_FORM_FIELD,
    SET_FORM_FIELD_INVALID,
    RESET_FORM_FIELD_VALIDITY,
    SET_FORM_VALUES,
    SET_BILLING_ADDRESS_SAME_AS_SHIPPING,
    SET_STRIPE_PAYMENT_METHOD,
    SET_EDITING_CHECKOUT_SHIPPING_ADDRESS,
    SET_PAYMENT_PAGE,
    SET_ITEM_CATALOG,
    SET_SELECTED_ITEM,
    SET_ITEM_VARIANT,
    SET_SHIPPING_METHOD,
    SET_ITEM_QUANTITY,
    SET_ITEM_UNIT_COST,
    SET_ITEM_TAX,
    SET_CREDIT_CARD_OPTION,
    SET_CREDIT_CARDS,
    OPEN_PAYMENTS_MODAL,
    CLOSE_PAYMENTS_MODAL,
    CLEAR_ORDER_DATA,
    SET_AUTH_POPUP_VISIBILITY,
    SET_REQUIRED_FIELDS,
    SET_FIELD_VALIDATIONS,
    OPEN_PAYMENT_OAUTH_WINDOW,
    CLOSE_PAYMENT_OAUTH_WINDOW,
    SET_USER_AUTHENTICATED,
    SET_IS_USER_EDITING_ITEM_DETAILS,
    SET_FORM_PROCESSING,
    SET_STRIPE_PROPERTIES,
    SET_STRIPE_FIELD_COMPLETE_STATUS,
    SET_ACCOUNT_NETWORKS,
    PAYMENT_MOUSE_ENTERED,
    PAYMENT_MOUSE_EXITED,
    SET_ITEM_TRANSACTION_ID,
    SET_ITEM_TRANSACTION_STATUS,
    SET_API_KEY,
    UPDATE_HAS_EXPRESS_CHECKOUT,
    UPDATE_HAS_REQUIRED_FIELDS,
    SET_SHIPPING_METHODS_OPTIONS,
    RESET_PAYMENT_FORM_VALUES,
    SET_PAYMENT_TRANSACTION_TIMESTAMP,
    SET_PAYMENT_ENABLED,
    SET_IS_ITEM_DESCRIPTION_TRUNCATED,
} from './paymentActions'

const INITIAL_STATE = Map({
    apiKey: undefined,
    isPaymentEnabled: false,
    lastPaymentTransactionTimestamp: undefined,
    isPaymentModalOpen: false,
    isPaymentHotspotHovered: false,
    isItemDescriptionTruncated: true,
    paymentPageName: GOTO_ITEM_DETAILS_PAGE,
    [PaymentConstants.formNames.SHIPPING_ADDRESS]: Map({
        values: Map({
            fullName: '',
            address: '',
            zipcode: '',
            town: '',
            country: 'United States',
            state: '',
            email: '',
            phone: '',
        }),
        errors: Map({}),
        errorMessage: '',
        hasRequiredFields: false,
        requiredFields: List([]),
        validationFields: List([]),
        isFormProcessing: false,
    }),
    [PaymentConstants.formNames.BILLING_ADDRESS]: Map({
        sameAsShippingAddress: true,
        values: Map({
            fullName: '',
            address: '',
            zipcode: '',
            town: '',
            country: 'United States',
            state: '',
            email: '',
            phone: '',
        }),
        errors: Map({}),
        hasRequiredFields: false,
        requiredFields: List([]),
        validationFields: List([]),
        isFormProcessing: false,
    }),
    [PaymentConstants.formNames.STRIPE_PAYMENT_METHOD]: Map({
        values: Map({
            name: '',
        }),
        errors: Map({}),
        errorMessage: '',
        creditCards: Map({}),
        selectedCreditCardIndex: 0,
        stripeFieldCompleteStatus: Map({
            [PaymentConstants.stripeFieldNames.CARD_HOLDER_NAME]: false,
            [PaymentConstants.stripeFieldNames.CARD_NUMBER]: false,
            [PaymentConstants.stripeFieldNames.CARD_EXP]: false,
            [PaymentConstants.stripeFieldNames.CARD_CVC]: false,
        }),
        stripeProperties: Map({
            shouldSubmit: false,
            cardName: '',
        }),
        isFormProcessing: false,
    }),
    [PaymentConstants.formNames.CHECKOUT]: Map({
        editingShippingAddress: false,
        shippingMethods: List([]),
        errors: Map({}),
        errorMessage: '',
        isFormProcessing: false,
        itemTransactionId: null,
        itemTransactionStatus: null,
    }),
    itemCostCalculator: Map({
        currentShippingMethod: '',
        itemUnitCost: 0,
        itemQuantity: 1,
        itemTax: 0,
        itemShippingCost: 0,
        itemPurchaseCost: 0,
    }),
    selectedItemDetails: Map({
        title: undefined,
        description: undefined,
        imageUrl: undefined,
        size: undefined,
        color: undefined,
        hexColor: undefined,
        variants: [],
        disabledVariants: [],
        isItemEnabled: false,
        payments_product_id: undefined,
        attributes: undefined,
        enabledVariants: undefined,
        attributeOptions: undefined,
        selectedVariants: undefined,
        payments_product_sku_id: undefined,
        sku: undefined,
        inventoryQuantity: undefined,
    }),
    itemCatalog: Map({}),
    isUserEditingItemDetails: false,
    isUserAuthenticated: false,
    isUserAuthPopUpVisible: false,
    OAuthWindowStatus: Map({
        isOpen: false,
        authType: null,
    }),
    accountNetworks: Map({}),
    hasExpressCheckout: false,
})

export const PaymentReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case SET_PAYMENT_ENABLED:
            return state.set('isPaymentEnabled', action.enabled)
        case SET_PAYMENT_TRANSACTION_TIMESTAMP:
            return state.set('lastPaymentTransactionTimestamp', action.timestamp)
        case SET_API_KEY:
            return state.set('apiKey', action.APIKey)
        case SET_ACCOUNT_NETWORKS:
            return state.set('accountNetworks', Map(action.networks))
        case SET_IS_ITEM_DESCRIPTION_TRUNCATED:
            return state.set('isItemDescriptionTruncated', action.isTruncated)
        case UPDATE_HAS_EXPRESS_CHECKOUT:
            const { id } = state.getIn(['accountNetworks', PaymentConstants.networks.STRIPE]) || {}
            const creditCards = state.getIn([PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, 'creditCards'])
            const hasExpressCheckout = id !== undefined && creditCards.size > 0

            return state.set('hasExpressCheckout', hasExpressCheckout)
        case UPDATE_HAS_REQUIRED_FIELDS:
            const hasValidAndRequiredFields =
                hasRequiredFields(state, action.formName) && hasValidFields(state, action.formName)

            return state.setIn([action.formName, 'hasRequiredFields'], hasValidAndRequiredFields)
        case CHANGE_FORM_FIELD:
            return state.setIn([action.formName, 'values', action.fieldId], action.value)
        case SET_REQUIRED_FIELDS:
            return state.setIn([action.formName, 'requiredFields'], List(action.requiredFields))
        case SET_FIELD_VALIDATIONS:
            return state.setIn([action.formName, 'validationFields'], List(action.validationFields))
        case SET_FORM_FIELD_INVALID:
            return state.withMutations(mState => {
                mState.mergeIn(
                    [action.formName, 'errors'],
                    Map({
                        [action.fieldId]: action.message,
                    })
                )

                const formErrors = mState.getIn([action.formName, 'errors'])
                const errorMessage = formErrors.get(formErrors.keySeq().first())

                mState.setIn([action.formName, 'errorMessage'], errorMessage)

                return mState
            })
        case RESET_FORM_FIELD_VALIDITY:
            return state.withMutations(mState => {
                const errors = mState.getIn([action.formName, 'errors']).delete(action.fieldId)

                return mState.setIn([action.formName, 'errors'], errors).setIn([action.formName, 'errorMessage'], '')
            })
        case SET_FORM_VALUES:
            return state.mergeIn([action.formName, 'values'], Map(action.values))
        case SET_BILLING_ADDRESS_SAME_AS_SHIPPING:
            return state.setIn([PaymentConstants.formNames.BILLING_ADDRESS, 'sameAsShippingAddress'], action.enabled)
        case SET_STRIPE_PAYMENT_METHOD:
            return state.setIn([PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, 'values'], Map(action.values))
        case RESET_PAYMENT_FORM_VALUES:
            return state.setIn([action.formName, 'values'], INITIAL_STATE.getIn([action.formName, 'values']))
        case SET_EDITING_CHECKOUT_SHIPPING_ADDRESS:
            return state.setIn([PaymentConstants.formNames.CHECKOUT, 'editingShippingAddress'], action.editing)
        case SET_PAYMENT_PAGE:
            return state.merge(state.set('paymentPageName', action.pageName))
        case SET_ITEM_CATALOG:
            return state.set('itemCatalog', Map(action.catalog))
        case SET_SELECTED_ITEM:
            const item = state.getIn(['itemCatalog', action.itemId])
            const variantAttributes = getVariantAttributes({ item })

            const selectedVariants = variantAttributes.attributesKeys.reduce(
                (acc, attributeKey) => ({
                    [attributeKey]: item.attributes.values[attributeKey][0],
                    ...acc,
                }),
                {}
            )

            const {
                payments_product_sku_id: selectedPaymentsProductSkuID,
                sku: selectedSkuID,
                is_enabled: selectedIsEnabled,
                quantity: selectedQuantity,
            } = item.variants.find(
                variant =>
                    (variant[variantAttributes.attributesKeys[0]] || {}).label ==
                        (selectedVariants || {})[variantAttributes.attributesKeys[0]] &&
                    (variant[variantAttributes.attributesKeys[1]] || {}).label ==
                        (selectedVariants || {})[variantAttributes.attributesKeys[1]]
            )

            const disabledSelectedVariants = getDisabledVariants({ selectedVariants, variantList: item.variants })

            const isSelectedItemEnabled = selectedIsEnabled && selectedQuantity != 0

            return state.withMutations(mState => {
                return mState
                    .set(
                        'selectedItemDetails',
                        Map({
                            ...variantAttributes,
                            ...item,
                            isItemEnabled: isSelectedItemEnabled,
                            selectedVariants,
                            disabledVariants: disabledSelectedVariants,
                            payments_product_sku_id: selectedPaymentsProductSkuID,
                            sku: selectedSkuID,
                            inventoryQuantity: selectedQuantity,
                        })
                    )
                    .setIn(['itemCostCalculator', 'itemQuantity'], 1)
            })
        case SET_ITEM_VARIANT:
            const { variantKey, variantValue } = action

            return state.withMutations(mState => {
                mState.setIn(['selectedItemDetails', 'selectedVariants'], {
                    ...mState.getIn(['selectedItemDetails', 'selectedVariants']),
                    [variantKey]: variantValue,
                })

                const attributesKeys = mState.getIn(['selectedItemDetails', 'attributesKeys'])
                const selectedItemVariants = mState.getIn(['selectedItemDetails', 'selectedVariants'])
                const { payments_product_sku_id, sku, is_enabled, quantity } = state
                    .getIn(['selectedItemDetails', 'variants'])
                    .find(
                        variant =>
                            variant[attributesKeys[0]].label == selectedItemVariants[attributesKeys[0]] &&
                            variant[attributesKeys[1]].label == selectedItemVariants[attributesKeys[1]]
                    )

                const isItemEnabled = is_enabled && quantity != 0

                return mState
                    .setIn(['selectedItemDetails', 'payments_product_sku_id'], payments_product_sku_id)
                    .setIn(['selectedItemDetails', 'sku'], sku)
                    .setIn(['selectedItemDetails', 'inventoryQuantity'], quantity)
                    .setIn(['selectedItemDetails', 'isItemEnabled'], isItemEnabled)
                    .setIn(
                        ['selectedItemDetails', 'disabledVariants'],
                        getDisabledVariants({
                            selectedVariants: selectedItemVariants,
                            variantList: mState.getIn(['selectedItemDetails', 'variants']),
                        })
                    )
            })
        case SET_SHIPPING_METHOD:
            //Update current shipping id
            const newCheckoutData = calculateItemCost({
                currentData: state.get('itemCostCalculator'),
                currentShippingMethod: action.shippingMethodId,
                itemShippingCost: action.price,
            })
            return state.set('itemCostCalculator', newCheckoutData)
        case SET_ITEM_QUANTITY:
            //Update current shipping id
            const updatedItemQuantity = calculateItemCost({
                currentData: state.get('itemCostCalculator'),
                itemQuantity: action.quantity,
            })
            return state.set('itemCostCalculator', updatedItemQuantity)
        case SET_ITEM_TAX:
            //Update TAX
            const updatedItemTax = calculateItemCost({
                currentData: state.get('itemCostCalculator'),
                itemTax: action.itemTax,
            })
            return state.set('itemCostCalculator', updatedItemTax)
        case SET_ITEM_TRANSACTION_ID:
            return state.setIn([PaymentConstants.formNames.CHECKOUT, 'itemTransactionId'], action.transactionId)
        case SET_ITEM_TRANSACTION_STATUS:
            return state.setIn([PaymentConstants.formNames.CHECKOUT, 'itemTransactionStatus'], action.status)
        case SET_ITEM_UNIT_COST:
            const updatedItemUnitCost = calculateItemCost({
                currentData: state.get('itemCostCalculator'),
                itemUnitCost: action.itemUnitCost,
            })
            return state.set('itemCostCalculator', updatedItemUnitCost)
        case SET_CREDIT_CARD_OPTION:
            const creditCardOptions = state
                .get(PaymentConstants.formNames.STRIPE_PAYMENT_METHOD)
                .set('selectedCreditCardIndex', action.value)

            return state.set(PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, creditCardOptions)
        case SET_CREDIT_CARDS:
            //the creditcards should come already sorted from the server
            return state.setIn(
                [PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, 'creditCards'],
                Map(action.creditCards)
            )
        case OPEN_PAYMENTS_MODAL:
            return state.set('isPaymentModalOpen', true)
        case CLOSE_PAYMENTS_MODAL:
            return state.set('isPaymentModalOpen', false)
        case CLEAR_ORDER_DATA:
            return state.withMutations(mState => {
                const hasExpressCheckout = mState.get('hasExpressCheckout')
                const isUserAuthenticated = mState.get('isUserAuthenticated')

                const isOauth = isUserAuthenticated && hasExpressCheckout

                //Clear form data
                const formsToClear = isOauth
                    ? // Only clear billing address for auth
                      [PaymentConstants.formNames.BILLING_ADDRESS]
                    : // For guest clear both billing and payment method
                      [PaymentConstants.formNames.BILLING_ADDRESS, PaymentConstants.formNames.STRIPE_PAYMENT_METHOD]

                for (let formName of formsToClear) {
                    mState.set(formName, INITIAL_STATE.get(formName))
                }

                //Clear item quantity
                const defaultShippingMethod = mState.getIn(['checkout', 'shippingMethods', 0])
                const checkoutData = calculateItemCost({
                    currentData: mState.get('itemCostCalculator'),
                    currentShippingMethod: defaultShippingMethod.id,
                    itemShippingCost: defaultShippingMethod.price,
                    itemQuantity: 1,
                })

                return mState.set('itemCostCalculator', checkoutData)
            })
        case SET_AUTH_POPUP_VISIBILITY:
            return state.set('isUserAuthPopUpVisible', action.isVisible)
        case SET_USER_AUTHENTICATED:
            return state.set('isUserAuthenticated', action.isAuthenticated)
        case OPEN_PAYMENT_OAUTH_WINDOW:
        case CLOSE_PAYMENT_OAUTH_WINDOW:
            return state.set('OAuthWindowStatus', state.get('OAuthWindowStatus').merge(Map(action.oauthState)))
        case SET_IS_USER_EDITING_ITEM_DETAILS:
            return state.set('isUserEditingItemDetails', action.isEditing)
        case SET_FORM_PROCESSING:
            return state.setIn([action.formName, 'isFormProcessing'], action.isProcessing)
        case SET_STRIPE_PROPERTIES:
            return state.mergeIn(
                [PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, 'stripeProperties'],
                Map(action.stripeProperties)
            )
        case SET_STRIPE_FIELD_COMPLETE_STATUS:
            return state.setIn(
                [PaymentConstants.formNames.STRIPE_PAYMENT_METHOD, 'stripeFieldCompleteStatus', action.fieldName],
                action.isComplete
            )
        case PAYMENT_MOUSE_ENTERED:
            return state.set('isPaymentHotspotHovered', true)
        case PAYMENT_MOUSE_EXITED:
            return state.set('isPaymentHotspotHovered', false)
        case SET_SHIPPING_METHODS_OPTIONS:
            return state.setIn([PaymentConstants.formNames.CHECKOUT, 'shippingMethods'], List(action.shippingOptions))
        default:
            return state
    }
}
